import React, { useEffect, useRef } from 'react';
import { ChevronDown, ClipboardList, Map, Target, TrendingUp, Zap } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useProjectStore } from '../store/useProjectStore';
import { ProjectJourneySidebar } from './ProjectJourneySidebar';

const STEP_META = [
  { label: 'Diagnóstico e Takt', icon: Target },
  { label: 'Mapeamento Atual (As-Is)', icon: Map },
  { label: 'Identificação de Mudas', icon: Zap },
  { label: 'Desenho do Futuro (To-Be)', icon: TrendingUp },
  { label: 'Plano de Implementação', icon: ClipboardList },
];

type JourneyPanelProps = {
  visible: boolean;
  expanded: boolean;
  showDeviationAlert?: boolean;
  onGenerateActionPlan?: () => void | Promise<void>;
  onToggle: () => void;
};

export const JourneyPanel: React.FC<JourneyPanelProps> = ({
  visible,
  expanded,
  showDeviationAlert = false,
  onGenerateActionPlan,
  onToggle,
}) => {
  const { isDark } = useTheme();
  const mapType = useProjectStore((state) => state.mapType);
  const stage = useProjectStore((state) => state.stage);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Minimiza ao clicar fora do painel enquanto estiver expandido.
  useEffect(() => {
    if (!expanded) return;
    const handlePointerDown = (event: Event) => {
      const target = event.target as Node | null;
      if (!target) return;
      const container = containerRef.current;
      if (container && container.contains(target)) return;
      // Ignora cliques em portais (modais, dropdowns) que vivem fora do DOM do painel
      const el = target as HTMLElement;
      if (el.closest?.('[data-journey-portal="true"]')) return;
      onToggle();
    };
    // Capture phase + pointerdown garante o disparo mesmo quando o canvas
    // (ReactFlow) chama stopPropagation em mousedown.
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [expanded, onToggle]);

  if (!visible) return null;

  // Compute variables first so they're available in both render paths
  const stageToIndex: Record<string, number> = {
    diagnosis: 1,
    kaizen: 2,
    planning: 3,
    execution: 4,
    sustainment: 5,
  };

  const currentIndex = stageToIndex[stage] || 1;
  const step = STEP_META[Math.max(0, Math.min(STEP_META.length - 1, currentIndex - 1))];
  const StepIcon = step.icon;
  const progressWidth = `${(currentIndex / STEP_META.length) * 100}%`;
  const headerBg = isDark && mapType === 'FACTORY' ? '#0f172a' : isDark ? '#111827' : '#f1f5f9';
  const headerBorder = isDark ? 'rgba(51, 65, 85, 0.95)' : 'rgba(203, 213, 225, 1)';
  const panelBg = isDark ? '#1e293b' : '#ffffff';
  const panelBorder = isDark ? 'rgba(51, 65, 85, 0.9)' : 'rgba(226, 232, 240, 1)';

  // Minimized → compact vertical capsule
  if (!expanded) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: 8,
          width: '100%',
          height: '100%',
          pointerEvents: 'auto',
        }}
      >
        <button
          type="button"
          onClick={onToggle}
          title="Expandir painel de jornada"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 5,
            padding: '10px 6px',
            background: headerBg,
            border: `1px solid ${headerBorder}`,
            borderRadius: 6,
            cursor: 'pointer',
            color: isDark ? '#e2e8f0' : '#334155',
            boxShadow: isDark
              ? '0 1px 4px rgba(0,0,0,0.35)'
              : '0 1px 3px rgba(0,0,0,0.10)',
          }}
        >
          <StepIcon size={13} />
          <span
            style={{
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              opacity: 0.65,
            }}
          >
            Jornada
          </span>
          {showDeviationAlert && (
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: '#facc15',
                flexShrink: 0,
              }}
            />
          )}
        </button>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        zIndex: 200,
        pointerEvents: 'none',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        ...(expanded ? { flex: 1, minHeight: 0 } : {})
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        style={{
          width: '100%',
          pointerEvents: 'auto',
          background: headerBg,
          color: isDark ? '#e2e8f0' : '#0f172a',
          borderLeft: `1px solid ${headerBorder}`,
          borderBottom: 'none',
          borderRadius: 0,
          boxShadow: 'none',
          overflow: 'hidden',
          cursor: 'pointer',
        }}
      >
        <div className="flex items-center gap-2 px-3 py-2 text-left">
          <div
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-sm border"
            style={{
              borderColor: isDark ? 'rgba(71, 85, 105, 0.9)' : 'rgba(203, 213, 225, 0.95)',
              background: isDark ? 'rgba(30, 41, 59, 0.9)' : 'rgba(255,255,255,0.75)',
            }}
          >
            <StepIcon size={13} />
          </div>

          <div className="min-w-0 flex-1">
            <div className="font-mono text-[9px] font-semibold uppercase tracking-[0.14em] opacity-70">Etapa {currentIndex}</div>
            <div className="truncate text-[12px] font-semibold leading-tight">{step.label}</div>
          </div>

          <div className="flex h-7 w-7 shrink-0 items-center justify-center">
            <ChevronDown size={14} className={`transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
          </div>
        </div>

        <div style={{ height: '2px', width: '100%', background: isDark ? 'rgba(30, 41, 59, 1)' : 'rgba(226, 232, 240, 1)' }}>
          <div
            style={{
              width: progressWidth,
              height: '100%',
              background: showDeviationAlert ? '#facc15' : '#22d3ee',
              transition: 'width 180ms ease',
            }}
          />
        </div>
      </button>

      <div
        style={{
          pointerEvents: expanded ? 'auto' : 'none',
          opacity: expanded ? 1 : 0,
          flex: expanded ? 1 : 0,
          minHeight: 0,
          transform: expanded ? 'translateY(0)' : 'translateY(-6px)',
          transition: 'flex 180ms ease, opacity 120ms ease, transform 180ms ease',
          overflow: 'hidden',
          background: panelBg,
          borderLeft: `1px solid ${panelBorder}`,
          boxShadow: 'none',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ flex: 1, minHeight: 0 }}>
          <ProjectJourneySidebar
            isOpen={true}
            onClose={onToggle}
            showDeviationAlert={showDeviationAlert}
            showTopChrome={false}
            onGenerateActionPlan={onGenerateActionPlan}
          />
        </div>
      </div>
    </div>
  );
};
